import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
    Container, Typography, CircularProgress, Alert, Tooltip,
    IconButton, Box, Stack, Link, Avatar, Button, Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    InfoOutlined, Warning,
    KeyboardArrowDown, KeyboardArrowUp,
} from '@mui/icons-material';
import { Surface } from '../components/primitives';

const SCORE_THRESHOLD = 3.0;
const TOP_N = 3;

const gradeColor = (grade, theme) => {
    if (!grade || grade === 'N/A') return theme.palette.text.muted;
    if (grade.startsWith('A')) return theme.palette.success.main;
    if (grade.startsWith('B')) return theme.palette.primary.light;
    if (grade.startsWith('C')) return theme.palette.text.secondary;
    return theme.palette.error.main;
};

// Merge buyers_with_grades (has grade) with bullish_activity (has dates)
const enrichBuyers = (buyersWithGrades, bullishActivity) => {
    if (!buyersWithGrades || !bullishActivity) return buyersWithGrades || [];

    return buyersWithGrades.map(buyer => {
        const trades = bullishActivity
            .filter(t => t.member === buyer.name)
            .sort((a, b) => (b.transaction_date || '').localeCompare(a.transaction_date || ''));

        const mostRecent = trades[0];
        return {
            ...buyer,
            member_id: mostRecent?.member_id || null,
            transaction_date: mostRecent?.transaction_date || null,
            trade_count: trades.length,
        };
    }).sort((a, b) => (b.transaction_date || '').localeCompare(a.transaction_date || ''));
};

// Format date as "Mar 19, 2026"
const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return dateStr;
    }
};

// --- Pick Card (featured recommendation) ---

const PickCard = ({ stock, pickNumber, totalScored, theme }) => {
    const logoUrl = `https://img.logo.dev/ticker/${stock.ticker}?token=pk_Fy4ZV5RMQEKzNJfSy9h6BA`;
    const buyers = enrichBuyers(stock.buyers_with_grades, stock.bullish_activity);
    const numBuyers = stock.num_buyers || 0;
    const numSellers = stock.num_sellers || 0;
    const percentile = totalScored > 0
        ? ((1 - stock.rank / totalScored) * 100).toFixed(1)
        : '?';

    const topBuyers = buyers.filter(b => b.grade && b.grade.startsWith('A'));

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: pickNumber * 0.1 }}
        >
            <Surface
                padding={0}
                sx={{
                    overflow: 'hidden',
                    border: t => `1px solid ${t.palette.primary.dark}40`,
                    '&:hover': { borderColor: 'primary.main' },
                    transition: t => `border-color ${t.customTransitions.base}`,
                }}
            >
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
                    background: 'linear-gradient(180deg, rgba(99,102,241,0.06) 0%, transparent 100%)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                                src={logoUrl}
                                alt={stock.ticker}
                                sx={{ width: 52, height: 52, bgcolor: '#fff' }}
                            />
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{
                                        fontFamily: t => t.typography.fontFamilyMono,
                                        fontSize: '1.5rem',
                                        fontWeight: 700,
                                        letterSpacing: '-0.02em',
                                        color: 'text.primary',
                                    }}>
                                        {stock.ticker}
                                    </Typography>
                                    <Box sx={{
                                        px: 1, py: 0.25, borderRadius: 0.5,
                                        bgcolor: 'rgba(99,102,241,0.1)',
                                        border: '1px solid rgba(99,102,241,0.25)',
                                    }}>
                                        <Typography variant="caption" sx={{
                                            color: 'primary.light', fontWeight: 600,
                                            fontFamily: t => t.typography.fontFamilyMono,
                                        }}>
                                            PICK #{pickNumber}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Typography variant="caption" sx={{
                                    color: 'text.muted',
                                    fontFamily: t => t.typography.fontFamilyMono,
                                }}>
                                    Rank #{stock.rank} of {totalScored} · Top {percentile}%
                                </Typography>
                            </Box>
                        </Box>
                        {numBuyers >= 3 && (
                            <Box sx={{
                                px: 1.5, py: 0.75, borderRadius: 1,
                                bgcolor: 'rgba(34,197,94,0.1)',
                                border: '1px solid rgba(34,197,94,0.25)',
                                textAlign: 'center',
                            }}>
                                <Typography sx={{
                                    color: 'success.main', fontWeight: 700,
                                    fontFamily: t => t.typography.fontFamilyMono,
                                    fontSize: '1rem', lineHeight: 1,
                                }}>
                                    {numBuyers}
                                </Typography>
                                <Typography variant="caption" sx={{
                                    color: 'success.main', fontWeight: 500,
                                    fontSize: '0.625rem',
                                }}>
                                    members buying
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Why this stock */}
                    <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            <strong>{numBuyers} member{numBuyers !== 1 ? 's' : ''} buying</strong>
                            {numSellers > 0 && (
                                <Typography component="span" variant="body2" sx={{ color: 'text.muted' }}>
                                    {' · '}{numSellers} selling · net {numBuyers > numSellers ? 'bullish' : 'bearish'} ({Math.abs(numBuyers - numSellers)} net)
                                </Typography>
                            )}
                        </Typography>
                        {topBuyers.length > 0 && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {topBuyers.length} of {numBuyers} buyers rated A or higher (top 15% of all Congress traders)
                            </Typography>
                        )}
                        {numBuyers >= 3 && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Consensus signal active — {numBuyers} independent members converging on the same stock
                            </Typography>
                        )}
                    </Stack>
                </Box>

                {/* Buyers with grades and dates */}
                <Box sx={{ p: 3 }}>
                    <Typography variant="overline" sx={{
                        color: 'text.secondary', display: 'block', mb: 1.5,
                    }}>
                        Who's buying
                    </Typography>
                    <Stack spacing={0.75}>
                        {buyers.map((b, i) => (
                            <Box
                                key={i}
                                component={b.member_id ? RouterLink : 'div'}
                                to={b.member_id ? `/congressmember/${b.member_id}` : undefined}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    px: 1.5, py: 0.75, borderRadius: 1,
                                    bgcolor: 'custom.surface.subtle',
                                    border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                    textDecoration: 'none',
                                    cursor: b.member_id ? 'pointer' : 'default',
                                    transition: t => `border-color ${t.customTransitions.fast}`,
                                    '&:hover': b.member_id ? {
                                        borderColor: 'custom.border.default',
                                        bgcolor: 'custom.surface.elevated',
                                    } : {},
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {b.grade && b.grade !== 'N/A' && (
                                        <Typography sx={{
                                            color: gradeColor(b.grade, theme),
                                            fontWeight: 700,
                                            fontFamily: t => t.typography.fontFamilyMono,
                                            fontSize: '0.8125rem',
                                            minWidth: 24,
                                        }}>
                                            {b.grade}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" sx={{
                                        color: 'text.primary',
                                        '&:hover': b.member_id ? { color: 'primary.light' } : {},
                                    }}>
                                        {b.name}
                                    </Typography>
                                    {b.trade_count > 1 && (
                                        <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                            ({b.trade_count} trades)
                                        </Typography>
                                    )}
                                </Box>
                                {b.transaction_date && (
                                    <Typography variant="caption" sx={{
                                        color: 'text.muted',
                                        fontFamily: t => t.typography.fontFamilyMono,
                                    }}>
                                        bought {formatDate(b.transaction_date)}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Surface>
        </motion.div>
    );
};

// --- Compact row for full rankings ---

const RankingRow = ({ stock }) => {
    const theme = useTheme();
    const logoUrl = `https://img.logo.dev/ticker/${stock.ticker}?token=pk_Fy4ZV5RMQEKzNJfSy9h6BA`;
    const numBuyers = stock.num_buyers || stock.congress_members_purchased?.length || 0;
    const numSellers = stock.num_sellers || stock.congress_members_sold?.length || 0;

    // Top buyer grade
    const topGrade = (stock.buyers_with_grades || [])
        .map(b => b.grade)
        .filter(g => g && g !== 'N/A')
        .sort()[0] || null;

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            px: 2.5, py: 1.25,
            borderBottom: t => `1px solid ${t.palette.custom.border.subtle}`,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
        }}>
            <Typography sx={{
                fontFamily: t => t.typography.fontFamilyMono,
                color: 'text.muted', fontSize: '0.8125rem', width: 36, textAlign: 'right', flexShrink: 0,
            }}>
                #{stock.rank}
            </Typography>
            <Avatar src={logoUrl} alt={stock.ticker} sx={{ width: 28, height: 28, bgcolor: '#fff', flexShrink: 0 }} />
            <Typography sx={{
                fontFamily: t => t.typography.fontFamilyMono,
                fontWeight: 600, color: 'text.primary', minWidth: 65, flexShrink: 0,
            }}>
                {stock.ticker}
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.muted' }}>
                    {numBuyers} buying · {numSellers} selling
                </Typography>
                {topGrade && (
                    <Typography variant="caption" sx={{
                        color: gradeColor(topGrade, theme),
                        fontFamily: t => t.typography.fontFamilyMono,
                        fontWeight: 600,
                    }}>
                        best: {topGrade}
                    </Typography>
                )}
            </Box>
            <Typography variant="caption" sx={{
                fontFamily: t => t.typography.fontFamilyMono,
                color: stock.score > SCORE_THRESHOLD ? 'success.main' : 'text.secondary',
                fontWeight: stock.score > SCORE_THRESHOLD ? 600 : 400,
                flexShrink: 0,
            }}>
                {stock.score?.toFixed(1)}
            </Typography>
        </Box>
    );
};

// --- Empty state ---

const NoPicks = () => (
    <Surface padding={4} sx={{ textAlign: 'center' }}>
        <Typography variant="h3" sx={{ color: 'text.primary', mb: 1.5 }}>
            No stocks currently meet our recommendation threshold
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 520, mx: 'auto', mb: 2, lineHeight: 1.7 }}>
            Our algorithm only recommends stocks when multiple skilled Congress members
            converge on the same stock with high confidence. When nothing qualifies,
            the recommended approach is to hold the S&P 500.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.muted', lineHeight: 1.7 }}>
            This selectivity is by design — it's part of what delivers a 77.7% hit rate.
        </Typography>
    </Surface>
);

// --- Main page ---

function StockRankings() {
    const theme = useTheme();
    const [rankings, setRankings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFullRankings, setShowFullRankings] = useState(false);

    useEffect(() => {
        const fetchStockRankings = async () => {
            try {
                const session = await fetchAuthSession();
                if (session?.tokens?.idToken) {
                    const idToken = session.tokens.idToken.toString();
                    const response = await fetch('https://api.capitolgains.io/user/rankings', {
                        method: 'GET',
                        headers: { Authorization: `Bearer ${idToken}` },
                    });
                    if (response.status === 403) {
                        setError({ code: 'pro_required' });
                        return;
                    }
                    if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
                    const data = await response.json();
                    if (data.rankings && Array.isArray(data.rankings)) {
                        setRankings(data.rankings);
                    } else {
                        throw new Error('Unexpected API response format');
                    }
                } else {
                    throw new Error('Session is invalid');
                }
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };
        fetchStockRankings();
    }, []);

    if (loading) {
        return (
            <Container sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error?.code === 'pro_required') {
        return (
            <Container sx={{ pt: 6 }}>
                <Alert
                    severity="info"
                    action={
                        <Button component={RouterLink} to="/pricing" variant="contained" size="small">
                            Upgrade
                        </Button>
                    }
                >
                    Stock rankings are a Capitol Gains Pro feature.
                </Alert>
            </Container>
        );
    }

    if (error) {
        return (
            <Container sx={{ pt: 6 }}>
                <Alert severity="error">Error: {error.message}</Alert>
            </Container>
        );
    }

    const sorted = [...rankings].sort((a, b) => b.score - a.score);
    const totalScored = sorted.length > 0 ? (sorted[0].total_scored || sorted.length) : 0;

    // Current picks: score > threshold, top N
    const picks = sorted.filter(s => s.score > SCORE_THRESHOLD).slice(0, TOP_N);

    return (
        <Container maxWidth="lg" sx={{ pt: 6, pb: 8 }}>
            {/* Page header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="h1" sx={{ color: 'text.primary' }}>
                        Stock Rankings
                    </Typography>
                    <Tooltip title={
                        <>
                            <Typography variant="body2" color="inherit">
                                Stocks ranked by congressional trading activity, weighted by trader skill and consensus.
                            </Typography>
                            <Link component={RouterLink} to="/track-record" sx={{ fontSize: '0.8125rem', mt: 0.5, display: 'block' }}>
                                See track record →
                            </Link>
                        </>
                    }>
                        <IconButton size="small" sx={{ color: 'text.muted' }}>
                            <InfoOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Scoring {totalScored} stocks · Top picks selected when multiple skilled traders converge ·{' '}
                    <Link component={RouterLink} to="/track-record" sx={{ fontSize: 'inherit' }}>
                        View backtested track record →
                    </Link>
                </Typography>
            </Box>

            {/* Disclaimer */}
            <Surface variant="subtle" padding={2} sx={{ mb: 4 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Warning sx={{ color: 'text.muted', fontSize: 18, mt: '2px' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                        Rankings are for informational purposes only and do not constitute financial advice.
                        Trader grades (A+ to F) reflect historical risk-adjusted returns.
                    </Typography>
                </Stack>
            </Surface>

            {/* Current Picks */}
            {picks.length > 0 ? (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h3" sx={{ color: 'text.primary', mb: 0.5 }}>
                        Top Picks
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                        {picks.length} stock{picks.length !== 1 ? 's' : ''} with the strongest congressional trading signal right now
                    </Typography>
                    <Stack spacing={2}>
                        {picks.map((stock, i) => (
                            <PickCard
                                key={stock.ticker}
                                stock={{ ...stock, rank: sorted.indexOf(stock) + 1 }}
                                pickNumber={i + 1}
                                totalScored={totalScored}
                                theme={theme}
                            />
                        ))}
                    </Stack>
                </Box>
            ) : (
                <Box sx={{ mb: 4 }}>
                    <NoPicks />
                </Box>
            )}

            {/* Full Rankings (collapsed) */}
            <Surface padding={0}>
                <Box
                    onClick={() => setShowFullRankings(!showFullRankings)}
                    sx={{
                        p: 2.5, cursor: 'pointer',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                    }}
                >
                    <Box>
                        <Typography variant="h4" sx={{ color: 'text.primary' }}>
                            Full Rankings
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.muted' }}>
                            All {totalScored} scored stocks, ranked by congressional trading signal
                        </Typography>
                    </Box>
                    <Button
                        variant="text" size="small"
                        endIcon={showFullRankings ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    >
                        {showFullRankings ? 'Hide' : 'Show'}
                    </Button>
                </Box>
                <Collapse in={showFullRankings}>
                    {sorted.map((stock, i) => (
                        <RankingRow
                            key={stock.ticker}
                            stock={{ ...stock, rank: i + 1 }}
                        />
                    ))}
                </Collapse>
            </Surface>
        </Container>
    );
}

export default StockRankings;
